<template>
  <section class="downloads">
    <div class="container">
      <!-- Header. -->
      <div class="downloads__header">
        <div class="downloads__type">
          <a :href="articleTypeUrl" class="downloads__type-link">
            {{ articleType }}
          </a>
        </div>
        <h1
          class="downloads__title title-custom"
          v-html="colorTitle(title)"></h1>

        <!-- Tab titles. -->
        <div
          v-if="!loading"
          ref="linksContainer"
          class="paragraph-tabs__tab-links-container scroll scroll--start">
          <div class="paragraph-tabs__tab-links" @scroll="handleScroll">
            <template v-for="(tab, idx) in tabs" :key="tab.id">
              <a
                :data-active="tab.id"
                ref="links"
                :class="{ 'paragraph-tabs__tab-link--active': idx === 0 }"
                class="paragraph-tabs__tab-link"
                @click="activeTabId = tab.id">
                {{ tab.name }}
              </a>
            </template>
          </div>
        </div>
      </div>

      <!-- Content. -->
      <div class="downloads__content">
        <Loading v-if="loading" />
        <div
          v-else
          class="downloads__results paragraph-tabs__field-tabs-item paragraph-tabs__field-items">
          <div class="tabs__results">
            <TransitionGroup name="toggle-height" appear>
              <template v-for="(tab, idx) in tabs" :key="tab.id + idx">
                <div v-if="activeTabId === tab.id" class="tabs__result-item">
                  <!-- Search form. -->
                  <div class="downloads__filter-form">
                    <input
                      type="text"
                      class="downloads__filter-form-input"
                      :placeholder="`${t.search[tab.id]} ...`"
                      :title="`${t.search[tab.id]} ...`"
                      v-model="searchInput" />
                    <span class="downloads__filter-form-icon"></span>
                  </div>
                  <!-- Results. -->
                  <div
                    v-for="(items, letter, idx) in filteredResults"
                    :key="tab.id + idx * Math.random() + letter"
                    class="downloads__result-letter-wrapper">
                    <div class="downloads__result-items">
                      <h3 class="downloads__result-letter">{{ letter }}</h3>
                      <TransitionGroup name="toggle-height" tag="span" appear>
                        <a
                          v-for="(item, key) in items"
                          :key="`${key}${item.id}`"
                          :href="item.url ?? '#'"
                          :target="
                            isMedia(item) && !isPasswordProtected(item)
                              ? '_blank'
                              : false
                          "
                          :download="
                            isMedia(item) && !isPasswordProtected(item)
                              ? ''
                              : false
                          "
                          class="downloads__result-item"
                          @click="toggleModalDisplay($event, item)">
                          <template v-if="isMedia(item)">
                            <div
                              v-if="item.images.length"
                              class="downloads-product__image">
                              <ResponsiveImage :images="item.images" />
                            </div>
                            <div
                              v-else
                              class="downloads-product__image downloads-product__image--no-img"></div>
                          </template>
                          <!-- Names of product items can contain HTML tags such as <sup>. -->
                          <div
                            class="downloads-product__filename"
                            v-html="getMediaName(item)"></div>
                          <span class="downloads-product__download"></span>
                        </a>
                      </TransitionGroup>
                    </div>
                  </div>
                </div>
              </template>
            </TransitionGroup>
          </div>

          <!-- No results. -->
          <div
            v-if="showEmpty"
            class="downloads__results--empty downloads__results--empty-filter">
            {{ t.noResults }}
          </div>

          <!-- Material Safety Data Sheets modal. -->
          <template
            v-if="
              activeTabId === msdsTabId &&
              Object.keys(selectedProduct).length !== 0
            ">
            <DialogModal
              v-model="modalOpen"
              modal-type="msds"
              :product="selectedProduct"
              ref="msdsModal"
              :lang="lang">
            </DialogModal>
          </template>

          <!-- Password Protected Media modal. -->
          <template v-if="selectedPasswordProtectedItem">
            <DownloadPasswordProtectedMediaModal
              ref="passwordModal"
              :media-id="
                selectedPasswordProtectedItem.file_id ||
                selectedPasswordProtectedItem.id
              "
              @download-success="handleDownloadSuccess"
              @download-error="handleDownloadError" />
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// components
import DialogModal from './elements/DialogModal';
import Loading from './elements/Loading';
import ResponsiveImage from './elements/ResponsiveImage';
import DownloadPasswordProtectedMediaModal from './DownloadPasswordProtectedMediaModal';
// utils
import { getDownloadsData } from '../utils/api';
import translationStrings from '../translations/translationStrings';

export default {
  name: 'downloads',
  components: {
    DialogModal,
    Loading,
    ResponsiveImage,
    DownloadPasswordProtectedMediaModal,
  },
  props: {
    articleType: {
      type: String,
      required: true,
    },
    articleTypeUrl: {
      type: String,
      required: true,
    },
    lang: {
      required: true,
      type: String,
    },
    title: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      modalOpen: false, // = IS-OPEN
      passwordModalOpen: false,
      msdsModal: null,
      passwordModal: null,
      searchInput: '',
      selectedProduct: {},
      selectedPasswordProtectedItem: null,
      activeTabId: 1, // 1-based
      tabs: [],
      msdsTabId: 0,
      t: translationStrings,
    };
  },
  computed: {
    filteredResults() {
      const activeTab = this.tabs.find((tab) => tab.id === this.activeTabId);
      if (activeTab) {
        // Return all active tab items if the search input is empty.
        if (this.searchInput === '') {
          return activeTab.items;
        }

        let finalResults = {};
        Object.entries(activeTab.items).forEach(([letter, items]) => {
          const matches = items.filter((item) =>
            this.cleanTitle(item.name)
              .toLowerCase()
              .includes(this.searchInput.toLowerCase()),
          );
          // If there are any matches, attach them to the results by letter.
          if (matches.length) {
            finalResults = { ...finalResults, ...{ [letter]: matches } };
          }
        });

        return finalResults;
      }
      // Fallback (sometimes tabs need some time to load).
      return this.tabs[0]?.items || [];
    },
    showEmpty: {
      get() {
        return !Object.keys(this.filteredResults).length;
      },
      set(value) {
        return value;
      },
    },
  },
  watch: {
    activeTabId(newActive, oldActive) {
      this.searchInput = '';
      this.showEmpty = false;

      let links = [];
      // @todo Use object destructuring.
      if (this.$refs.links != null) {
        // eslint-disable-next-line prefer-destructuring
        links = this.$refs.links;
      }

      const oldTab = [...links].find((l) => +l.dataset.active === oldActive);
      const newTab = [...links].find((l) => +l.dataset.active === newActive);

      if (oldTab == null && newTab == null) return;

      // handle green line animations
      // @todo refactor at some point, PLEEEAAASSSSEEEEEEEEE!
      if (oldActive < newActive) {
        oldTab.classList.add('paragraph-tabs__tab-link--to-left');
        oldTab.classList.remove('paragraph-tabs__tab-link--active');

        newTab.classList.add('paragraph-tabs__tab-link--to-right');
        setTimeout(() => {
          newTab.classList.add('paragraph-tabs__tab-link--active');
        }, 400);
      } else if (oldActive > newActive) {
        oldTab.classList.add('paragraph-tabs__tab-link--to-right');
        oldTab.classList.remove('paragraph-tabs__tab-link--active');

        newTab.classList.add('paragraph-tabs__tab-link--to-left');
        setTimeout(() => {
          newTab.classList.add('paragraph-tabs__tab-link--active');
        }, 400);
      }

      setTimeout(() => {
        [oldTab, newTab].forEach((tab) =>
          tab.classList.remove(
            'paragraph-tabs__tab-link--to-left',
            'paragraph-tabs__tab-link--to-right',
          ),
        );
      }, 400);
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const { data } = await getDownloadsData(this.lang);
        this.tabs = data;
        // MSDS items require special handling, identify the tab that holds them.
        const msdsTab = this.tabs.find((tab) => {
          return tab.hasOwnProperty('msds') && tab.msds;
        });
        if (msdsTab) {
          this.msdsTabId = msdsTab.id;
        }
      } catch (error) {
        if (error.response) {
          console.error(error.response);
        }
      } finally {
        this.activeTabId = this.tabs[0]?.id;
        this.loading = false;
      }
    },
    handleScroll({ target }) {
      const container = this.$refs.linksContainer;
      if (target.offsetWidth + target.scrollLeft >= target.scrollWidth) {
        container.classList.add('scroll--end');
        container.classList.remove('scroll--start');
      } else if (target.scrollLeft === 0) {
        container.classList.add('scroll--start');
        container.classList.remove('scroll--end');
      } else {
        container.classList.add('scroll', 'scroll--end', 'scroll--start');
      }
    },
    toggleModalDisplay(e, item) {
      // Handle password protected media items
      if (item.protected) {
        e.preventDefault();
        this.selectedPasswordProtectedItem = item;

        this.$nextTick(() => {
          if (this.$refs.passwordModal) {
            this.$refs.passwordModal.openPasswordModal();
          }
        });
        return;
      }

      // Exit if the item is not MSDS.
      if (!item.hasOwnProperty('msds')) {
        return true;
      }

      e.preventDefault();
      this.modalOpen = !this.modalOpen;

      // Set the selected product to show the proper data.
      this.selectedProduct = item;

      this.$nextTick(() => {
        if (this.$refs.msdsModal) {
          this.$refs.msdsModal.openDialogModal();
        }
      });
    },
    handleDownloadSuccess() {
      // Close the password modal on successful download
      this.selectedPasswordProtectedItem = null;
      // Modal will close itself via the modal component
    },
    handleDownloadError(error) {
      // Handle download error - you might want to show an error message
      console.error('Download failed:', error);
      // Optionally keep the modal open so user can try again
    },
    isMedia(item) {
      // Only media items will have 'images' key.
      return item.hasOwnProperty('images');
    },
    isPasswordProtected(item) {
      return !!item.protected;
    },
    getMediaName(item) {
      return item.protected
        ? `${item.name}<i class="far fa-lock-keyhole"></i>`
        : item.name;
    },
    cleanTitle(title) {
      return title
        .replace(/\™|®|©|&trade;|&reg;|&copy;|&#8482;|&#174;|&#169;/g, '')
        .replace(/<[^>]*>?/gm, '');
    },
    colorTitle(string) {
      const split = string.split(' ');
      if (split.length > 1) {
        const last = split.pop();
        const rest = split.join(' ');
        return `${rest} <span>${last}</span>`;
      }

      return string;
    },
  },
};
</script>
